import React, { useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const MAX_PARTICLES = 2000;
const GRAVITY = -15;
const DRAG = 0.96;

const COLOR_CYAN = new THREE.Color('#06b6d4');
const COLOR_MAGENTA = new THREE.Color('#ec4899');
const COLOR_YELLOW = new THREE.Color('#fbbf24');
const COLOR_WHITE = new THREE.Color('#ffffff');

const FireworksDisplay = forwardRef((props, ref) => {
    const meshRef = useRef();

    // Particle State
    const particles = useMemo(() => {
        return new Array(MAX_PARTICLES).fill(0).map(() => ({
            position: new THREE.Vector3(),
            velocity: new THREE.Vector3(),
            color: new THREE.Color(),
            scale: 0,
            life: 0, // 0 = dead, >0 = alive (time remaining)
            maxLife: 1
        }));
    }, []);

    const dummy = useMemo(() => new THREE.Object3D(), []);

    useImperativeHandle(ref, () => ({
        // Tier 1: Small Spark Burst (Enhanced)
        spawnSparkBurst: (position, color = COLOR_MAGENTA, count = 14) => {
            spawnParticles(position, color, count, {
                speed: 12, // Faster explosion
                spread: 1.5, // Wider spread
                life: 0.6, // Slightly longer visibility
                scale: 0.5, // Larger particles for mobile readability
                upwardBias: 8 // Significant upward pop
            });
        },
        // Tier 2: Rocket / Ring
        spawnRocket: (position, color = COLOR_CYAN) => {
            // Initial upward burst
            spawnParticles(position, color, 15, {
                speed: 15,
                spread: 0.5,
                life: 0.8,
                scale: 0.5,
                upwardBias: 10
            });
        },
        // Tier 3: Big Show
        spawnShow: (position, color = COLOR_YELLOW) => {
            spawnParticles(position, color, 50, {
                speed: 20,
                spread: 3,
                life: 1.5,
                scale: 0.8
            });
        },
        // Tier 3: Focus Complete Sequence (Choreographed)
        spawnFocusSequence: () => {
            // Phase 1: Ignition (0s) - Letters & Brain
            // Brain
            spawnParticles([0, 0.1, 0], COLOR_CYAN, 20, { speed: 8, life: 0.5, scale: 0.4 });
            // Letters (Approximate positions based on MindstormPinball.js)
            const letterPositions = [
                [-4.125, 0.1, -11.25], // F
                [-1.375, 0.1, -6.25],  // O
                [4.125, 0.1, -1.25],   // C
                [4.125, 0.1, 3.75],    // U
                [1.375, 0.1, 8.75]     // S
            ];
            letterPositions.forEach((pos, i) => {
                const color = i % 2 === 0 ? COLOR_MAGENTA : COLOR_YELLOW;
                spawnParticles(pos, color, 15, { speed: 6, life: 0.5, scale: 0.4, upwardBias: 5 });
            });

            // Phase 2: Spread (0.5s - 1.5s) - Rockets
            setTimeout(() => {
                // Scoreboard area
                spawnParticles([0, 5, -25], COLOR_CYAN, 30, { speed: 15, life: 1.0, scale: 0.6, upwardBias: 10 });
            }, 500);
            setTimeout(() => {
                // Left Wall
                spawnParticles([-10, 5, -10], COLOR_MAGENTA, 25, { speed: 12, life: 0.8, scale: 0.5, upwardBias: 8 });
            }, 700);
            setTimeout(() => {
                // Right Wall
                spawnParticles([10, 5, -10], COLOR_YELLOW, 25, { speed: 12, life: 0.8, scale: 0.5, upwardBias: 8 });
            }, 900);
            setTimeout(() => {
                // Extra Area
                spawnParticles([2, 4, -18], COLOR_WHITE, 25, { speed: 12, life: 0.8, scale: 0.5, upwardBias: 8 });
            }, 1100);

            // Phase 3: Finale (1.5s) - Big Sky Bursts
            setTimeout(() => {
                // Left Sky
                spawnParticles([-8, 8, -20], COLOR_CYAN, 40, { speed: 22, life: 1.5, scale: 0.9, spread: 4 });
            }, 1500);
            setTimeout(() => {
                // Right Sky
                spawnParticles([8, 8, -20], COLOR_MAGENTA, 40, { speed: 22, life: 1.5, scale: 0.9, spread: 4 });
            }, 1700);
            setTimeout(() => {
                // Center Top Sky
                spawnParticles([0, 10, -25], COLOR_YELLOW, 60, { speed: 25, life: 2.0, scale: 1.0, spread: 5 });
            }, 2000);
        },
        trigger: (type, position, color) => {
            // Generic trigger for easier integration
            // ...
        }
    }));

    const spawnParticles = (pos, color, count, options = {}) => {
        const { speed = 10, spread = 1, life = 1, scale = 0.5, upwardBias = 0 } = options;

        let spawned = 0;
        for (let i = 0; i < MAX_PARTICLES; i++) {
            if (particles[i].life <= 0) {
                const p = particles[i];
                // Reset
                p.life = life + Math.random() * 0.2; // Randomize life slightly
                p.maxLife = p.life;
                p.position.copy(new THREE.Vector3(...pos));
                p.color.copy(color);
                p.scale = scale;

                // Random Velocity
                const theta = Math.random() * Math.PI * 2;
                const phi = Math.random() * Math.PI;
                const r = Math.random() * speed;

                p.velocity.set(
                    r * Math.sin(phi) * Math.cos(theta),
                    r * Math.sin(phi) * Math.sin(theta) + upwardBias,
                    r * Math.cos(phi)
                );

                // Add spread
                p.position.x += (Math.random() - 0.5) * spread;
                p.position.y += (Math.random() - 0.5) * spread;
                p.position.z += (Math.random() - 0.5) * spread;

                spawned++;
                if (spawned >= count) break;
            }
        }
    };

    useFrame((state, delta) => {
        if (!meshRef.current) return;

        let activeCount = 0;

        particles.forEach((p, i) => {
            if (p.life > 0) {
                // Physics
                p.velocity.y += GRAVITY * delta;
                p.velocity.multiplyScalar(DRAG);
                p.position.addScaledVector(p.velocity, delta);

                p.life -= delta;

                // Render
                dummy.position.copy(p.position);

                // Scale down as life fades
                const scale = p.scale * (p.life / p.maxLife);
                dummy.scale.set(scale, scale, scale);

                dummy.updateMatrix();
                meshRef.current.setMatrixAt(i, dummy.matrix);
                meshRef.current.setColorAt(i, p.color);

                activeCount++;
            } else {
                // Hide dead particles
                dummy.scale.set(0, 0, 0);
                dummy.updateMatrix();
                meshRef.current.setMatrixAt(i, dummy.matrix);
            }
        });

        meshRef.current.instanceMatrix.needsUpdate = activeCount > 0;
        if (activeCount > 0) meshRef.current.instanceColor.needsUpdate = true;
    });

    return (
        <instancedMesh ref={meshRef} args={[null, null, MAX_PARTICLES]}>
            <boxGeometry args={[0.5, 0.5, 0.5]} />
            <meshBasicMaterial toneMapped={false} transparent opacity={0.8} />
        </instancedMesh>
    );
});

export default FireworksDisplay;
